import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FileText, CalendarDays } from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatDate } from '@/lib/utils';

interface Psur {
  id: string;
  product: string | null;
  reportNumber: string | null;
  interval: string | null;
  dlp: string | null;
  dueAt: string | null;
  status: string | null;
}

export function PsurListPage() {
  const psurs = useQuery({
    queryKey: ['psurs'],
    queryFn: async () => {
      try {
        return (await api.get('/api/psur-draft?pageSize=100')).data;
      } catch {
        return { items: [], total: 0 };
      }
    },
  });

  const items: Psur[] = psurs.data?.items ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">PSURs</h1>
          <p className="text-sm text-muted-foreground">
            ICH E2C(R2) Periodic Benefit-Risk Evaluation Reports - 19 sections with QC and e-sign.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/psur-calendar">
            <CalendarDays className="mr-1.5 h-4 w-4" /> Calendar
          </Link>
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>PSUR reports</CardTitle>
          <CardDescription>
            Reporting interval governs frequency (6-monthly, yearly, 3-yearly).
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Product</TableHead>
                <TableHead>Report #</TableHead>
                <TableHead>Interval</TableHead>
                <TableHead>DLP</TableHead>
                <TableHead>Due</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {psurs.isLoading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-sm text-muted-foreground">
                    Loading...
                  </TableCell>
                </TableRow>
              )}
              {!psurs.isLoading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="p-10 text-center text-sm text-muted-foreground">
                    <FileText className="mx-auto mb-2 h-6 w-6" /> No PSURs yet.
                  </TableCell>
                </TableRow>
              )}
              {items.map((p) => (
                <TableRow key={p.id}>
                  <TableCell className="font-medium">{p.product ?? '-'}</TableCell>
                  <TableCell className="font-mono text-xs">{p.reportNumber ?? '-'}</TableCell>
                  <TableCell className="text-sm">{p.interval ?? '-'}</TableCell>
                  <TableCell className="text-xs">{p.dlp ? formatDate(p.dlp) : '-'}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {p.dueAt ? formatDate(p.dueAt) : '-'}
                  </TableCell>
                  <TableCell>
                    <StatusBadge s={p.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button asChild variant="outline" size="sm">
                      <Link to={`/psur/${p.id}`}>Open</Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function StatusBadge({ s }: { s: string | null }) {
  if (!s) return <span className="text-muted-foreground">-</span>;
  const v: Record<string, 'secondary' | 'info' | 'success' | 'warning' | 'destructive'> = {
    DRAFT: 'secondary',
    IN_PROGRESS: 'info',
    QC: 'warning',
    QC_COMPLETE: 'info',
    SUBMITTED: 'success',
    APPROVED: 'success',
    ARCHIVED: 'secondary',
  };
  return <Badge variant={v[s] ?? 'secondary'}>{s.replace(/_/g, ' ')}</Badge>;
}
